/**
 * Product personalisation web component
 *
 * Used by three alternate product templates:
 *   - product.personalised-name        (data-mode="name")
 *   - product.personalised-name-number (data-mode="name-number")
 *   - product.shirt-printing           (data-mode="shirt")
 *
 * Markup is rendered by snippets/personalisation-*.liquid and wired to the
 * main buy button form via the HTML form attribute (data-form-id).
 *
 * Why this component exists
 * -------------------------
 * Theme product-form calls preventDefault() on submit, so native HTML5
 * validation on external inputs never runs. This component validates
 * personalisation fields in the capture phase before product-form handles
 * the submission.
 *
 * Shirt-printing add-on product
 * -----------------------------
 * Without Shopify Plus you cannot increase a line item price at checkout.
 * When a customer selects "Yes" on shirt-printing, we add TWO cart items:
 *   1. The main shirt (with Name/Number line item properties)
 *   2. A separate add-on variant from product.metafields.custom.personalise_add_on_product
 *
 * The add-on variant id is output in Liquid as data-addon-variant-id on
 * <product-personalisation>. The toggle label price is rendered in Liquid only.
 *
 * Submit flow
 * -----------
 *   1. bindForm() attaches one capture-phase listener per product form id
 *   2. handleFormSubmit() validates every product-personalisation on that form
 *   3. If validation fails → preventDefault, show error, stop
 *   4. If shirt mode + "Yes" → addToCartWithAddon() (JSON multi-item cart add)
 *   5. Otherwise → product-form continues with its normal single-item submit
 */
if (!customElements.get("product-personalisation")) {
  class ProductPersonalisation extends HTMLElement {
    /** @type {Set<string>} Form ids that already have a submit listener */
    static boundForms = new Set();

    /** Shirt-printing template mode; other modes are name and name-number */
    static SHIRT_MODE = "shirt";

    connectedCallback() {
      this.formId = this.dataset.formId;

      if (this.mode === ProductPersonalisation.SHIRT_MODE) {
        this.setupShirtToggle();
      }

      // Clear validation errors as the customer types
      this.querySelectorAll(
        '[data-personalisation-field], [data-personalisation-shirt-field], input[name^="properties["]',
      ).forEach((input) => {
        input.addEventListener("input", () => this.clearError());
      });

      // Register form submit handler once per associated product form
      if (this.formId) {
        ProductPersonalisation.bindForm(this.formId);
      }
    }

    /** @returns {'name' | 'name-number' | 'shirt' | undefined} */
    get mode() {
      return this.dataset.mode;
    }

    /**
     * Add-on variant id from custom.personalise_add_on_product metafield.
     * Set in personalisation-shirt.liquid; only used when mode is shirt.
     * @returns {string | undefined}
     */
    get addonVariantId() {
      return this.dataset.addonVariantId;
    }

    /**
     * Whether personalisation fields are active for this block.
     * Name modes are always active; shirt mode is active only when "Yes" is selected.
     */
    isEnabled() {
      if (this.mode !== ProductPersonalisation.SHIRT_MODE) return true;

      const yesInput = this.querySelector(
        '[data-personalisation-radio-input][value="Yes"]',
      );
      return yesInput && yesInput.checked;
    }

    /**
     * Inputs that must be filled before add-to-cart.
     * Shirt "No" returns an empty list (nothing to validate).
     */
    getInputs() {
      if (this.mode === ProductPersonalisation.SHIRT_MODE) {
        return this.isEnabled()
          ? this.querySelectorAll("[data-personalisation-shirt-field]")
          : [];
      }

      const byDataAttr = this.querySelectorAll("[data-personalisation-field]");
      if (byDataAttr.length) return byDataAttr;

      return this.querySelectorAll('input[name^="properties["]');
    }

    getFieldLabel(input) {
      const label = this.querySelector(`label[for="${input.id}"]`);
      return label ? label.textContent.trim() : input.name;
    }

    showError(message) {
      const errorEl = this.querySelector("[data-personalisation-error]");
      if (!errorEl) {
        alert(message);
        return;
      }
      errorEl.textContent = message;
      errorEl.classList.remove("hidden");
    }

    clearError() {
      const errorEl = this.querySelector("[data-personalisation-error]");
      if (!errorEl) return;
      errorEl.textContent = "";
      errorEl.classList.add("hidden");
    }

    /**
     * Validates visible personalisation inputs for this block.
     * @returns {boolean} false blocks form submission
     */
    validate() {
      this.clearError();
      const inputs = this.getInputs();

      for (const input of inputs) {
        if (!input.value.trim()) {
          this.showError(
            `Please make a selection for "${this.getFieldLabel(input)}".`,
          );
          input.focus();
          return false;
        }
      }

      return true;
    }

    /** Wires Yes/No radio to show/hide name and number fields on shirt-printing */
    setupShirtToggle() {
      this.setFieldsVisibility(this.isEnabled());

      this.querySelectorAll("[data-personalisation-radio-input]").forEach(
        (radio) => {
          radio.addEventListener("change", () => {
            this.clearError();
            this.setFieldsVisibility(this.isEnabled());
          });
        },
      );
    }

    /** Shows name/number fields when "Yes"; clears values when hidden */
    setFieldsVisibility(enabled) {
      const fields = this.querySelector("[data-personalisation-fields]");
      const inputs = this.querySelectorAll(
        "[data-personalisation-shirt-field]",
      );

      if (fields) {
        fields.classList.toggle(
          "product-personalisation__fields--hidden",
          !enabled,
        );
      }

      inputs.forEach((input) => {
        if (!enabled) {
          input.value = "";
        }
      });
    }

    /**
     * Builds line item properties for shirt personalisation (Name, Number).
     * @returns {Record<string, string>}
     */
    collectProperties() {
      const properties = {};

      this.querySelectorAll("[data-personalisation-shirt-field]").forEach(
        (input) => {
          const match = input.name.match(/\[(.*?)\]/);
          if (!match) return;
          properties[match[1]] = input.value;
        },
      );

      return properties;
    }

    /**
     * Shirt-printing only: adds main variant + add-on fee variant via JSON cart API.
     * Bypasses product-form because Shopify cart/add FormData only supports one item.
     */
    addToCartWithAddon(event, productForm) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.addonVariantId) {
        this.showError(
          "Personalisation is not available for this product. Please contact us for more information.",
        );
        return;
      }

      const variantInput = productForm.querySelector('[name="id"]');
      const quantityInput = productForm.querySelector('[name="quantity"]');
      const variantId = variantInput?.value ?? "";
      const quantity = quantityInput?.value ?? "1";
      const properties = this.collectProperties();
      const cart = ProductPersonalisation.getCartElement();
      const sections = ProductPersonalisation.getSectionIds(cart);

      const payload = {
        items: [
          {
            id: variantId,
            quantity,
            properties,
          },
          {
            id: this.addonVariantId,
            quantity,
          },
        ],
      };

      if (sections) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      ProductPersonalisation.setSubmitting(productForm, true);

      fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(
              response.description ||
                response.message ||
                "Unable to add to cart. Please contact us for support or try again later.",
            );
            return;
          }

          ProductPersonalisation.renderCart(response, cart);
        })
        .catch(() => {
          this.showError(
            "Unable to add to cart. Please contact us for support or try again later.",
          );
        })
        .finally(() => {
          ProductPersonalisation.setSubmitting(productForm, false);
        });
    }

    /** @param {string} formId */
    static getBlocksForForm(formId) {
      return document.querySelectorAll(
        `product-personalisation[data-form-id="${formId}"]`,
      );
    }

    /**
     * Attaches a single capture-phase submit listener to a product form.
     * Capture phase ensures validation runs before product-form.onSubmitHandler.
     * @param {string} formId
     */
    static bindForm(formId) {
      if (ProductPersonalisation.boundForms.has(formId)) return;

      const productForm = document
        .querySelector(`form#${formId}`)
        ?.closest("product-form");
      const form = document.getElementById(formId);

      if (!productForm || !form) return;

      ProductPersonalisation.boundForms.add(formId);

      form.addEventListener(
        "submit",
        (event) => {
          ProductPersonalisation.handleFormSubmit(event, productForm, formId);
        },
        true,
      );
    }

    /**
     * Validates all personalisation blocks, then delegates shirt "Yes" to addToCartWithAddon.
     * @param {SubmitEvent} event
     * @param {HTMLElement} productForm
     * @param {string} formId
     */
    static handleFormSubmit(event, productForm, formId) {
      const blocks = ProductPersonalisation.getBlocksForForm(formId);

      for (const block of blocks) {
        if (!block.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
      }

      const shirtBlock = Array.from(blocks).find(
        (block) => block.mode === ProductPersonalisation.SHIRT_MODE,
      );

      if (shirtBlock && shirtBlock.isEnabled()) {
        shirtBlock.addToCartWithAddon(event, productForm);
      }
    }

    static getCartElement() {
      return (
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer")
      );
    }

    /** Section ids needed to refresh the cart drawer after a JSON cart add */
    static getSectionIds(cart) {
      if (!cart || typeof cart.getSectionsToRender !== "function") return "";
      return cart
        .getSectionsToRender()
        .map((section) => section.section)
        .join(",");
    }

    /** Updates cart drawer/notification or redirects to /cart */
    static renderCart(response, cart) {
      if (
        typeof publish !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: String(response.items?.[0]?.variant_id ?? ""),
          cartData: response,
        });
      }

      if (cart) {
        cart.renderContents(response);
        return;
      }

      window.location = window.routes.cart_url;
    }

    /** Mirrors product-form loading state on the submit button */
    static setSubmitting(productForm, isSubmitting) {
      const submitButton = productForm.querySelector('[type="submit"]');
      const spinner = productForm.querySelector(".loading__spinner");
      const submitButtonText = submitButton?.querySelector("span");

      if (!submitButton) return;

      if (isSubmitting) {
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.classList.add("loading");
        spinner?.classList.remove("hidden");
        return;
      }

      submitButton.classList.remove("loading");
      submitButton.removeAttribute("aria-disabled");
      spinner?.classList.add("hidden");

      if (submitButtonText && window.variantStrings) {
        submitButtonText.textContent = window.variantStrings.addToCart;
      }
    }
  }

  customElements.define("product-personalisation", ProductPersonalisation);
}
